import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { orderService } from '../services/orderService';
import { Order } from '../services/adminService';
import { formatPrice, formatDateTime } from '../utils';

const OrderConfirmation: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'checking' | 'success' | 'failed'>('pending');

  useEffect(() => {
    if (orderId) {
      loadOrder();
    }
  }, [orderId]);

  const loadOrder = async () => {
    if (!orderId) return;
    
    const response = await orderService.getOrder(orderId);
    if (response.success && response.data) {
      setOrder(response.data);
      if (response.data.paymentStatus === 'paid') {
        setPaymentStatus('success');
      }
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mb-4"></div>
          <p className="text-gray-600">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center">
          <span className="text-6xl block mb-4">📦</span>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Order Not Found</h2>
          <p className="text-gray-600 mb-6">We couldn't find this order.</p>
          <Link to="/" className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all font-medium inline-block">
            Go to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Success Header */}
        <div className="bg-white rounded-2xl shadow-sm p-8 text-center mb-8">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-5xl">✓</span>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Order Confirmed!</h1>
          <p className="text-gray-600 mb-4">Thank you for your purchase</p>
          <p className="text-sm text-gray-500">
            Order #{order._id.slice(-8).toUpperCase()}
          </p>
        </div>

        {/* Payment Status */}
        {order.paymentStatus === 'pending' && (
          <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-6 mb-8">
            <div className="flex items-start space-x-3">
              <span className="text-3xl">⏳</span>
              <div>
                <h3 className="font-bold text-yellow-900 mb-1">Payment Pending</h3>
                <p className="text-sm text-yellow-800">
                  {order.paymentStatus === 'pending' && 'Waiting for M-Pesa payment confirmation'}
                </p>
                <p className="text-xs text-yellow-700 mt-2">
                  Check your phone for the M-Pesa prompt
                </p>
              </div>
            </div>
          </div>
        )}

        {order.paymentStatus === 'paid' && (
          <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6 mb-8">
            <div className="flex items-start space-x-3">
              <span className="text-3xl">✓</span>
              <div>
                <h3 className="font-bold text-green-900 mb-1">Payment Received</h3>
                <p className="text-sm text-green-800">Your payment has been confirmed</p>
              </div>
            </div>
          </div>
        )}

        {/* Order Details */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Order Details</h2>

          {/* Items */}
          <div className="space-y-3 mb-6">
            {order.products.map((item, idx) => (
              <div key={idx} className="flex justify-between items-center pb-3 border-b border-gray-200">
                <div className="flex-1">
                  <p className="font-medium text-gray-900">
                    {typeof item.product === 'object' ? item.product.name : 'Product'}
                  </p>
                  <p className="text-sm text-gray-600">Quantity: {item.quantity}</p>
                </div>
                <p className="font-bold text-gray-900">{formatPrice(item.price * item.quantity)}</p>
              </div>
            ))}
          </div>

          {/* Totals */}
          <div className="space-y-2 pt-4 border-t border-gray-200">
            <div className="flex justify-between">
              <span className="text-gray-700">Subtotal</span>
              <span className="font-medium">{formatPrice(order.totalAmount)}</span>
            </div>
            <div className="flex justify-between text-xl font-bold">
              <span>Total</span>
              <span className="text-green-600">{formatPrice(order.totalAmount)}</span>
            </div>
          </div>
        </div>

        {/* Delivery Info */}
        {order.deliveryAddress && (
          <div className="bg-white rounded-2xl shadow-sm p-6 mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Delivery Information</h2>
            <div className="space-y-2 text-sm">
              <p><strong>Address:</strong> {order.deliveryAddress}</p>
              <p><strong>Estimated Delivery:</strong> 2-3 business days</p>
              <p className="text-gray-600 mt-4">
                You'll receive SMS updates about your delivery status
              </p>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4">
          <Link
            to="/orders"
            className="flex-1 py-3 bg-green-600 text-white text-center rounded-lg hover:bg-green-700 transition-all font-medium"
          >
            View My Orders
          </Link>
          <Link
            to="/products"
            className="flex-1 py-3 border-2 border-green-600 text-green-600 text-center rounded-lg hover:bg-green-50 transition-all font-medium"
          >
            Continue Shopping
          </Link>
        </div>

        {/* Help */}
        <div className="mt-8 text-center text-sm text-gray-600">
          <p>Need help? Contact us at <a href="tel:+254700000000" className="text-green-600">+254 700 000 000</a></p>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmation;

